import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation } from "react-router-dom"
import cx from "classnames"
import { Check, Database, Edit3 } from "react-feather"
import {
  API_DATA,
  dataSourceGroups,
  dataSourceProgress,
  resolveDataSource
} from "../../utils/dataSources"
import s from "./DataSourceBadge.module.scss"

/**
 * Whether this screen's numbers are real — in the header, on every page.
 *
 * The app is mid-rebuild: some modules read the backend, some are the design
 * drawn against sample data. Sample data is realistic on purpose, which is
 * exactly what makes it dangerous — nothing on the screen tells you a reading
 * is a placeholder. This does, in one place, and opening it shows where the
 * rebuild has got to.
 *
 * Everything comes from `utils/dataSources`; there is nothing to keep in step
 * here when a module gets wired.
 */
const DataSourceBadge = () => {
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)
  const triggerRef = useRef(null)

  const current = useMemo(() => resolveDataSource(location.pathname), [location.pathname])
  const groups = useMemo(() => dataSourceGroups(), [])
  const progress = useMemo(() => dataSourceProgress(), [])

  const isApi = current.source === API_DATA
  const close = useCallback(() => setOpen(false), [])

  // Moving to another page shouldn't leave a panel open describing the last one.
  useEffect(() => setOpen(false), [location.pathname])

  useEffect(() => {
    if (!open) return undefined

    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) close()
    }
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return
      close()
      triggerRef.current?.focus()
    }

    document.addEventListener("mousedown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)

    return () => {
      document.removeEventListener("mousedown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open, close])

  return (
    <div className={s.root} ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        className={cx(s.pill, isApi ? s.api : s.sample)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={
          isApi
            ? "This page shows API data. Open the data source list."
            : "This page shows sample data. Open the data source list."
        }
        onClick={() => setOpen((current_) => !current_)}
      >
        {/* Steady when it's real data, breathing for sample — the state worth
            noticing is the one where the numbers aren't real. */}
        <span className={s.dot} aria-hidden="true" />
        <span className={s.pillText}>{isApi ? "API data" : "Sample data"}</span>
      </button>

      {open && (
        <div className={s.panel} role="dialog" aria-label="Data sources">
          <div className={s.head}>
            <span className={cx(s.headIcon, isApi ? s.api : s.sample)} aria-hidden="true">
              {isApi ? <Database size={16} /> : <Edit3 size={16} />}
            </span>
            <div>
              <h2 className={s.heading}>
                {current.label ?? "This page"} is showing {isApi ? "API data" : "sample data"}
              </h2>
              <p className={s.blurb}>
                {isApi
                  ? "Every figure here comes from the backend — the dev server, so these are real responses rather than production readings."
                  : "The layout is final, but the figures are placeholders — don’t act on them. This screen switches to real readings when its endpoints land."}
              </p>
            </div>
          </div>

          {/* How far the rebuild has got, counted off the list below rather
              than typed in beside it. */}
          <div className={s.progress}>
            <div className={s.progressHead}>
              <span>Modules connected</span>
              <b>
                {progress.connected} of {progress.total}
              </b>
            </div>
            <div
              className={s.progressTrack}
              role="progressbar"
              aria-valuenow={progress.connected}
              aria-valuemin={0}
              aria-valuemax={progress.total}
              aria-label="Modules connected to the backend"
            >
              <span className={s.progressFill} style={{ width: `${progress.percent}%` }} />
            </div>
          </div>

          <div className={s.lists}>
            <section className={s.group}>
              <h3 className={cx(s.groupTitle, s.api)}>
                <span className={s.dot} aria-hidden="true" />
                API data
              </h3>
              <ul className={s.items}>
                {groups.api.map((entry) => (
                  <li
                    key={entry.label}
                    className={cx(s.item, { [s.itemCurrent]: entry.label === current.label })}
                  >
                    {entry.label}
                    {entry.label === current.label && (
                      <Check size={13} className={s.itemMark} aria-label="You are here" />
                    )}
                  </li>
                ))}
              </ul>
            </section>

            <section className={s.group}>
              <h3 className={cx(s.groupTitle, s.sample)}>
                <span className={s.dot} aria-hidden="true" />
                Sample data
              </h3>
              <ul className={s.items}>
                {groups.sample.map((entry) => (
                  <li
                    key={entry.label}
                    className={cx(s.item, { [s.itemCurrent]: entry.label === current.label })}
                  >
                    {entry.label}
                    {entry.label === current.label && (
                      <Check size={13} className={s.itemMark} aria-label="You are here" />
                    )}
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </div>
      )}
    </div>
  )
}

export default DataSourceBadge
